import React, { FC, memo, useState, useEffect } from 'react';
import { ICheckoutPage, IAddCustomerInformationRequest } from '@montana/models/generated/cms.interface';
import { useCheckout } from '@montana/containers/checkout';
import { getCountries } from '@montana/services/checkout';
import { CustomerInformationForm } from './customer-information-form';
import { CheckoutB2CBase, CheckoutSteps } from '../checkout-b2c-base';
import { useRouter } from '@montana/containers/router';
import { useTranslations } from '@montana/containers/translations';
import { useCart } from '@montana/containers/cart';
import { useTracking } from '@montana/containers/tracking';
import { IFieldOptionValue } from '@montana/shared/form/field/field';
import { useConnectedServiceFunctions } from '@montana/shared/use-connected-service-functions';
import { useDynamicSettings } from '@montana/containers/dynamic-settings';
import {useSettings} from '@montana/containers/settings/settings.container';

export const CustomerInformationPage: FC<ICheckoutPage> = memo((props: ICheckoutPage) => {
    const [countries, setCountries] = useState<IFieldOptionValue[]>([]);
    const [ctaText, setCtaText] = useState('');
    const [submitForm, setSubmitForm] = useState(false);
    const [userSubmitted, setUserSubmitted] = useState(false);

    const { push } = useRouter();
    const translate = useTranslations();
    const { trackCheckoutStep } = useTracking();
    const { links } = useDynamicSettings();

    const {
        setCustomerInformation,
        setCustomerInformationSuccess,
        customerInformationLoaded,
        loadCustomerInformation,
        customerInformation
    } = useCheckout();
    const {market} = useSettings();

    const marketId = market?.marketId ?? ''
    const [cGetCountries] = useConnectedServiceFunctions(getCountries);

    const { cartGuid } = useCart();

    useEffect(() => {
        if (setCustomerInformationSuccess && userSubmitted) {
            push(links?.checkoutShippingPage ?? '/');
        }
    }, [setCustomerInformationSuccess, userSubmitted]);

    useEffect(() => {
        trackCheckoutStep(1);

        setCtaText(translate(t => t.checkoutPage.personalInformationProceedToShipping, '[Proceed to Shipping]'));

        cGetCountries(marketId)
            .then(response => {
                if (response.ok) {
                    response.json().then(countries => {
                        const countryOptions = Object.entries(countries).
                            map<IFieldOptionValue>(([key, value]): IFieldOptionValue => ({ value: key, text: `${value}` }));
                        setCountries(countryOptions);
                    });
                } else {
                    console.error('Failed to get countries');
                }
            })
            .catch(error => console.error('Failed to get countries', error));
    }, []);

    useEffect(() => {
        if (!!cartGuid) {
            loadCustomerInformation();
        }
    }, [cartGuid]);

    useEffect(() => {
        if (submitForm) {
            setSubmitForm(value => !value);
        }
    }, [submitForm]);

    function onPersonalInformationSubmit(customerInformation: IAddCustomerInformationRequest) {
        setUserSubmitted(true);
        setCustomerInformation({
            ...customerInformation,
            shippingAddress: customerInformation.billingAddressSameAsShipping ? {...customerInformation.billingAddress} : {...customerInformation.shippingAddress},
            billingAddressSameAsShipping: customerInformation.billingAddressSameAsShipping
        } as IAddCustomerInformationRequest);
    }

    return (
        <CheckoutB2CBase
            {...props}
            step={CheckoutSteps.PersonalInformation}
            ctaText={ctaText}
            onCTAClick={() => setSubmitForm(true)}
            showStepper
        >
            {customerInformationLoaded
                ? <CustomerInformationForm
                    className="checkout-b2c-base__personal-information"
                    countries={countries}
                    customerInformation={customerInformation}
                    onSubmit={onPersonalInformationSubmit}
                    submitForm={submitForm}
                    market={market}
                />
                : <></>
            }
        </CheckoutB2CBase>
    )
});
